import { ReactNode, useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AdminRefundRequestDto, RefundRequestStatus } from '@ivy-monorepo/shared'
import Table from '../../components/Table'
import Pagination from '../../components/Pagination'
import MultiSelectFilter from '../../components/admin/MultiSelectFilter'
import RefundRequestDetailModal from '../../components/modals/RefundRequestDetailModal'
import FeedbackThreadModal from '../../components/FeedbackThreadModal'
import { adminService } from '../../services/admin.service'
import { formatTicketPrice } from '../../utils/ticketPrice'
import { ALL_REFUND_STATUSES, REFUND_STATUS_LABELS } from '../../utils/refundStatus'

const REFUND_STATUS_OPTIONS = Object.values(RefundRequestStatus).map(status => ({
  value: status,
  label: REFUND_STATUS_LABELS[status],
}))

function parseStatusesFromParams(searchParams: URLSearchParams): RefundRequestStatus[] {
  const parsed = (searchParams.get('status') ?? '')
    .split(',')
    .map(part => part.trim())
    .filter((part): part is RefundRequestStatus => REFUND_STATUS_OPTIONS.some(option => option.value === part))

  return parsed.length > 0 ? parsed : ALL_REFUND_STATUSES
}

type RefundRequestsTabProps = {
  /** The Tickets page's tab buttons, rendered on the left of the tab bar. */
  tabs: ReactNode
}

/**
 * Tickets › Refunds. Shows every status by default (refunded and declined ones open read-only).
 * Order comes from the backend: requests waiting for a decision first, then refunds in flight,
 * failed ones, and resolved ones — newest first within each group.
 */
const RefundRequestsTab = ({ tabs }: RefundRequestsTabProps) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [requests, setRequests] = useState<AdminRefundRequestDto[]>([])
  const [pagination, setPagination] = useState({ total: 0, page: 1, pageSize: 10, totalPages: 0, hasNext: false, hasPrev: false })
  const [selectedPublicId, setSelectedPublicId] = useState<string | null>(null)
  const [openFeedbackCommentPublicId, setOpenFeedbackCommentPublicId] = useState<string | null>(null)
  // The refund request the open feedback thread came from — its modal reopens on Back.
  const [feedbackReturnPublicId, setFeedbackReturnPublicId] = useState<string | null>(null)

  const currentPage = parseInt(searchParams.get('page') || '1')
  const selectedStatuses = parseStatusesFromParams(searchParams)
  const statusFilter = selectedStatuses.join(',')

  const fetchRequests = useCallback(async () => {
    try {
      const statuses = statusFilter.split(',') as RefundRequestStatus[]
      const response = await adminService.getRefundRequests(currentPage, 10, statuses)
      setRequests(
        response.refundRequests.map(request => ({
          ...request,
          amountLabel: formatTicketPrice(request.amountCents),
          eventLabel: `${request.ticket.eventName} · ${request.ticket.quantity} × ${formatTicketPrice(request.ticket.priceCents)}`,
        }))
      )
      setPagination(response.pagination)
    } catch (err) {
      console.log('Failed to fetch refund requests', err)
    }
  }, [currentPage, statusFilter])

  useEffect(() => {
    fetchRequests()
  }, [fetchRequests])

  const updateSearchParams = (mutate: (params: URLSearchParams) => void) => {
    const nextParams = new URLSearchParams(searchParams)
    mutate(nextParams)
    setSearchParams(nextParams)
  }

  const handleStatusFilterChange = (statuses: RefundRequestStatus[]) => {
    updateSearchParams(params => {
      params.set('status', statuses.join(','))
      params.set('page', '1')
    })
  }

  const handlePageChange = (page: number) => {
    updateSearchParams(params => {
      params.set('page', page.toString())
    })
  }

  return (
    <div className="users-container">
      <div className="users-header">
        <h2>Tickets</h2>
        {pagination.totalPages > 1 && (
          <Pagination currentPage={pagination.page} totalPages={pagination.totalPages} onPageChange={handlePageChange} />
        )}
      </div>

      <div className="tab-bar" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        {tabs}
        <MultiSelectFilter
          title="Status"
          options={REFUND_STATUS_OPTIONS}
          selected={selectedStatuses}
          fallback={ALL_REFUND_STATUSES}
          onChange={handleStatusFilterChange}
        />
      </div>

      <div className="users-table-container">
        <div className="table-wrapper">
          <Table
            id="refund-requests"
            name="Refund requests"
            keyField="publicId"
            columns={[
              { label: '', accessor: 'user.imageUrl', type: 'userAvatar' },
              { label: 'User', accessor: 'user.lastName', type: 'fullNameAndTelephone' },
              { label: 'Status', accessor: 'status', type: 'refundStatus' },
              { label: 'Event', accessor: 'eventLabel', type: 'text' },
              { label: 'Amount', accessor: 'amountLabel', type: 'text' },
              { label: 'Reason', accessor: 'reason', type: 'text' },
              { label: 'Requested', accessor: 'createdAt', type: 'datetime' },
            ]}
            sourceData={requests}
            isPropertyBarVisible={false}
            filterTerms=""
            disableGlobalSearch={true}
            disableClientSort={true}
            onRowClick={(row: AdminRefundRequestDto) => setSelectedPublicId(row.publicId)}
          />
        </div>
      </div>

      <RefundRequestDetailModal
        isOpen={selectedPublicId !== null}
        refundRequestPublicId={selectedPublicId}
        onClose={() => setSelectedPublicId(null)}
        onChanged={fetchRequests}
        onOpenFeedback={commentPublicId => {
          setFeedbackReturnPublicId(selectedPublicId)
          setSelectedPublicId(null)
          setOpenFeedbackCommentPublicId(commentPublicId)
        }}
      />

      <FeedbackThreadModal
        commentPublicId={openFeedbackCommentPublicId}
        isOpen={openFeedbackCommentPublicId !== null}
        onClose={() => {
          setOpenFeedbackCommentPublicId(null)
          setFeedbackReturnPublicId(null)
        }}
        onChanged={fetchRequests}
        onBack={
          feedbackReturnPublicId
            ? () => {
                setOpenFeedbackCommentPublicId(null)
                setSelectedPublicId(feedbackReturnPublicId)
                setFeedbackReturnPublicId(null)
              }
            : undefined
        }
        backLabel="Back to refund request"
      />
    </div>
  )
}

export default RefundRequestsTab
